import { Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MLink from '@mui/material/Link';
import { makeStyles } from 'tss-react/mui';
import RadioGroupField from '../forms/radio-group-field';
import TextField from '../forms/text-field';
import TextAreaField from '../forms/text-area-field';
import GroupFormImage from './form-image';
import CityField from '../forms/city-field';
import FormSubmitStatus from '../forms/form-submit-status';
import FormSavingBar from '../forms/form-saving-bar';
import type { Group } from '../../types/Group';

export type GroupFormValues = {
  name: string;
  slug: string;
  description: string;
  memberDescription: string;
  public: boolean;
  discoverable: boolean;
  websiteUrl: string;
  instagramUsername: string;
  image: unknown;
  requestPrompt: string;
  createEventPermission: string;
  createPostPermission: string;
  uploadImagePermission: string;
  cityId: string;
};

export const groupSchema = ({ editing }: { editing?: boolean }) => Yup.object().shape({
  name: Yup.string().required('Name is required'),
  slug: Yup.string().nullable(),
  description: Yup.string().nullable(),
  memberDescription: Yup.string().nullable(),
  public: Yup.boolean(),
  websiteUrl: Yup.string().nullable(),
  instagramUsername: Yup.string().nullable(),
  image: Yup.mixed().nullable(),
  requestPrompt: Yup.string().nullable(),
  createEventPermission: Yup.string().required('Create event permission is required'),
  createPostPermission: Yup.string().required('Create post permission is required'),
  uploadImagePermission: Yup.string().required('Upload image permission is required'),
  ...(editing ? null : { cityId: Yup.string().required('City is required') })
});

const useStyles = makeStyles()((theme) => ({
  submitButtons: {
    margin: theme.spacing(1, 0),
  },
}));

type Props = {
  editing?: boolean;
  disabled?: boolean;
  group?: Group;
};

const GroupForm = ({ editing, disabled: formDisabled, group }: Props) => {
  const { isSubmitting, submitCount, values } = useFormikContext<GroupFormValues>();
  const { classes } = useStyles();
  const disabled = formDisabled || isSubmitting;
  const forceTouched = submitCount > 0;
  return (
    <Form>
      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          name="name"
          label="Name *"
          disabled={disabled}
          touched={forceTouched}
        />
        {editing && (
          <TextField
            name="slug"
            label="URL slug"
            helperText="Must contain only lower case letters and dashes (-)"
            disabled={disabled}
            touched={forceTouched}
          />
        )}
        <TextAreaField
          name="description"
          label="Public description"
          disabled={disabled}
          touched={forceTouched}
          helperText={(
            <span>
              <MLink href="https://www.markdownguide.org/basic-syntax/" target="_blank">
                Markdown
              </MLink>
              {' '}is supported
            </span>
          )}
        />
        <TextAreaField
          name="memberDescription"
          label="Additional members-only description"
          disabled={disabled}
          touched={forceTouched}
          helperText={(
            <span>
              Only visible to people who are members of the group.
              {' '}
              <MLink href="https://www.markdownguide.org/basic-syntax/" target="_blank">
                Markdown
              </MLink>
              {' '}is supported
            </span>
          )}
        />
        <TextField
          name="websiteUrl"
          label="Website URL"
          disabled={disabled}
        />
        <TextField
          name="instagramUsername"
          label="Instagram username"
          disabled={disabled}
          helperText={'Example: @everydayridescom'}
        />
        {!editing && (
          <CityField
            name="cityId"
            label="City"
            disabled={disabled}
            touched={forceTouched}
          />
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <GroupFormImage disabled={disabled} currentImage={group?.image} />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h2">Settings</Typography>
        <RadioGroupField
          name="createEventPermission"
          label="Who can create events?"
          disabled={disabled}
          touched={forceTouched}
          options={[
            {
              label: 'Owners only',
              value: 'OWNER',
            },
            {
              label: 'Any group member',
              value: 'MEMBER',
            },
            {
              label: 'Any group member after admin approval',
              value: 'MEMBER_WITH_APPROVAL',
            },
          ]}
        />
        <RadioGroupField
          name="createPostPermission"
          label="Who can create posts?"
          disabled={disabled}
          touched={forceTouched}
          options={[
            {
              label: 'Owners only',
              value: 'OWNER',
            },
            {
              label: 'Any group member',
              value: 'MEMBER',
            },
          ]}
        />
        <RadioGroupField
          name="uploadImagePermission"
          label="Who can upload images to the gallery?"
          disabled={disabled}
          touched={forceTouched}
          options={[
            {
              label: 'Owners only',
              value: 'OWNER',
            },
            {
              label: 'Any group member',
              value: 'MEMBER',
            },
          ]}
        />
        <RadioGroupField
          name="public"
          label="Privacy"
          disabled={disabled}
          touched={forceTouched}
          options={[
            {
              label: 'Public (anyone can join)',
              value: true
            },
            {
              label: 'Private (by invite or request to join)',
              value: false
            }
          ]}
        />
        {!values.public && (
          <TextAreaField
            name="requestPrompt"
            label="Join request prompt"
            disabled={disabled}
            touched={forceTouched}
            helperText={(
              <span>
                <MLink href="https://www.markdownguide.org/basic-syntax/" target="_blank">
                  Markdown
                </MLink>
                {' '}is supported
              </span>
            )}
          />
        )}
        <RadioGroupField
          name="discoverable"
          label="Visibility"
          disabled={disabled}
          touched={forceTouched}
          options={[
            {
              label: 'Discoverable (shows up in the groups list)',
              value: true
            },
            {
              label: 'Hidden (only people with the group link)',
              value: false
            }
          ]}
        />
      </Paper>

      <div className={classes.submitButtons}>
        <FormSavingBar />
        <Button
          type="submit"
          sx={{ mt: 2 }}
          disabled={disabled}
          color="primary"
          variant="contained"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
        <FormSubmitStatus />
      </div>
    </Form>
  );
};

export default GroupForm;
